import { useState, useMemo } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  CircularProgress,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { timeOffDayPortionLabel } from "../../../enums/HR/TimeOffDayPortionEnum";
import { DeliveryUnit } from "../../../data/HR/DeliveryUnit";

interface Availability {
  policy: string;
  startDate: Date;
  endDate: Date;
  isHalfDay: boolean;
  dayPortion: unknown;
}

interface PersonData {
  firstName: string;
  lastName: string;
  department?: string;
  deliveryUnit?: string;
  availabilities: Availability[];
}

type IntervalRecord = {
  name: string;
  policy: string;
  startDate: Date;
  endDate: Date;
  isHalfDay: boolean;
  dayPortion: unknown;
};

const departments = [
  "All",
  "Dispatch",
  "Brokerage",
  "Safety",
  "Accounting",
  "Data Analytics Department",
  "HR",
  "Permits",
  "IT",
  "Management",
  "Operations",
  "Fleet",
  "Truck Sales",
  "Insurance",
  "Intermodal",
];

const UNASSIGNED_DEPARTMENT = "Unassigned";

const groupByPerson = (data: any[]): Record<string, PersonData> =>
  data.reduce((acc: Record<string, PersonData>, record: any) => {
    const personKey = `${record.firstName}-${record.lastName}`;
    if (!acc[personKey]) {
      acc[personKey] = {
        firstName: record.firstName,
        lastName: record.lastName,
        department: record.department?.trim() || UNASSIGNED_DEPARTMENT,
        deliveryUnit: record.deliveryUnit,
        availabilities: [],
      };
    }
    acc[personKey].availabilities.push({
      policy: record.policy,
      startDate: new Date(record.startDate),
      endDate: new Date(record.endDate),
      isHalfDay: record.isHalfDay,
      dayPortion: record.dayPortion,
    });
    return acc;
  }, {});

const formatApiDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const fetchPeopleAvailability = async (
  token: string | null | undefined,
  startDate: Date,
  endDate: Date,
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const { data } = await axios.get(`${apiVariable}/employeesAvailability`, {
    headers,
    params: {
      startDate: formatApiDate(startDate),
      endDate: formatApiDate(endDate),
    },
  });
  return data;
};

const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const getMonthRange = (year: number, monthIndex: number) => {
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  return { start, end };
};

const getOverlap = (
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date,
): { overlapStart: Date; overlapEnd: Date } | null => {
  const overlapStart = start1 > start2 ? start1 : start2;
  const overlapEnd = end1 < end2 ? end1 : end2;
  return overlapStart <= overlapEnd ? { overlapStart, overlapEnd } : null;
};

const YearlyAvailability = () => {
  const { token } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");
  const [selectedDeliveryUnit, setSelectedDeliveryUnit] = useState<string>("");
  const [selectedIntervals, setSelectedIntervals] = useState<{
    department: string;
    month: number;
    intervals: IntervalRecord[];
  } | null>(null);

  const startOfYear = useMemo(() => new Date(year, 0, 1, 0, 0, 0, 0), [year]);
  const endOfYear = useMemo(
    () => new Date(year, 11, 31, 23, 59, 59, 999),
    [year],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["yearlyAvailability", year],
    queryFn: () => fetchPeopleAvailability(token, startOfYear, endOfYear),
    enabled: !!token,
  });

  const safeData = useMemo(() => data || [], [data]);
  const groupedData = useMemo(() => groupByPerson(safeData), [safeData]);
  const visibleDepartments = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set([
          ...departments.filter((department) => department !== "All"),
          ...safeData.map(
            (record: any) => record.department?.trim() || UNASSIGNED_DEPARTMENT,
          ),
        ]),
      ).sort((first, second) => first.localeCompare(second)),
    ],
    [safeData],
  );

  const filteredData: Record<string, PersonData> = useMemo(() => {
    return Object.entries(groupedData).reduce(
      (acc: Record<string, PersonData>, [personKey, personData]) => {
        if (
          selectedDepartment !== "All" &&
          personData.department !== selectedDepartment
        )
          return acc;

        if (
          selectedDeliveryUnit &&
          personData.deliveryUnit?.toLowerCase() !==
            selectedDeliveryUnit.toLowerCase()
        )
          return acc;

        const filteredAvailabilities = personData.availabilities.filter(
          (av) => av.endDate >= startOfYear && av.startDate <= endOfYear,
        );

        if (filteredAvailabilities.length) {
          acc[personKey] = {
            ...personData,
            availabilities: filteredAvailabilities,
          };
        }
        return acc;
      },
      {},
    );
  }, [
    groupedData,
    selectedDepartment,
    selectedDeliveryUnit,
    startOfYear,
    endOfYear,
  ]);

  const deptMonthAggregation = useMemo(() => {
    const result: Record<number, Record<string, IntervalRecord[]>> = {};
    const allMonthIndices = [...Array(12).keys()];

    Object.values(filteredData).forEach((person) => {
      const {
        firstName,
        lastName,
        department = UNASSIGNED_DEPARTMENT,
        availabilities,
      } = person;

      availabilities.forEach((av) => {
        const overallOverlap = getOverlap(
          av.startDate,
          av.endDate,
          startOfYear,
          endOfYear,
        );
        if (!overallOverlap) return;

        const { overlapStart: avStart, overlapEnd: avEnd } = overallOverlap;

        allMonthIndices.forEach((mIndex) => {
          const { start, end } = getMonthRange(year, mIndex);
          const monthOverlap = getOverlap(avStart, avEnd, start, end);
          if (!monthOverlap) return;

          result[mIndex] ??= {};
          result[mIndex][department] ??= [];
          result[mIndex][department].push({
            name: `${firstName} ${lastName}`,
            policy: av.policy,
            startDate: monthOverlap.overlapStart,
            endDate: monthOverlap.overlapEnd,
            isHalfDay: av.isHalfDay,
            dayPortion: av.dayPortion,
          });
        });
      });
    });

    return result;
  }, [filteredData, startOfYear, endOfYear, year]);

  const handlePrevYear = () => setYear((prev) => prev - 1);
  const handleNextYear = () => setYear((prev) => prev + 1);

  const buildTooltipContent = (intervals: IntervalRecord[]) => {
    if (intervals.length === 0) return <Typography>No one off</Typography>;

    const byPerson: Record<string, IntervalRecord[]> = {};
    intervals.forEach((int) => {
      byPerson[int.name] ??= [];
      byPerson[int.name].push(int);
    });

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Who is off:
        </Typography>
        <ul style={{ paddingLeft: "1em", margin: 0 }}>
          {Object.entries(byPerson).map(([person, list]) => (
            <li key={person} style={{ marginBottom: "4px" }}>
              <strong>{person}</strong>
              <ul style={{ paddingLeft: "1em", margin: 0 }}>
                {list.map((int, idx) => (
                  <li key={idx}>
                    <em>{camelCaseToSentenceCase(int.policy)}</em>:{" "}
                    {formatDate(int.startDate)} – {formatDate(int.endDate)}
                    {` (${timeOffDayPortionLabel(int.dayPortion, int.isHalfDay)})`}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Box>
    );
  };

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );

  if (isError)
    return (
      <Typography color="error" align="center">
        Error: {error instanceof Error ? error.message : "Unknown Error"}
      </Typography>
    );

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Box
        display="flex"
        justifyContent="center"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <FormControl sx={{ maxWidth: "100%", width: 200 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={selectedDepartment}
            label="Department"
            onChange={(event) => {
              setSelectedDepartment(event.target.value);
              setSelectedDeliveryUnit("");
            }}
          >
            {visibleDepartments.map((department) => (
              <MenuItem key={department} value={department}>
                {department}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ maxWidth: "100%", width: 200 }}>
          <InputLabel>Delivery Unit</InputLabel>
          <Select
            value={selectedDeliveryUnit}
            label="Delivery Unit"
            onChange={(event) => setSelectedDeliveryUnit(event.target.value)}
          >
            {DeliveryUnit.map((unit) => (
              <MenuItem key={unit.value} value={unit.value}>
                {unit.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
        <IconButton aria-label="Previous year" onClick={handlePrevYear}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="bold" sx={{ mx: 1 }}>
          {year}
        </Typography>
        <IconButton aria-label="Next year" onClick={handleNextYear}>
          <ArrowForward />
        </IconButton>
      </Box>

      <Paper
        elevation={3}
        role="region"
        aria-label="Yearly department availability"
        tabIndex={0}
        sx={{
          p: { xs: 1.5, sm: 3 },
          overflowX: "auto",
          overscrollBehaviorX: "contain",
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Department Aggregation (Monthly)
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Each cell shows how many <strong>people</strong> are off in that
          department &amp; month. Tap or hover over a count to see their
          intervals.
        </Typography>

        <Table sx={{ minWidth: 900, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: 150,
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <Typography fontWeight="bold">Department</Typography>
              </TableCell>
              {[...Array(12).keys()].map((mIndex) => (
                <TableCell
                  key={mIndex}
                  align="center"
                  sx={{ width: 60, borderRight: "1px solid #e0e0e0" }}
                >
                  <Typography variant="caption">
                    {new Date(year, mIndex).toLocaleString("en-US", {
                      month: "short",
                    })}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleDepartments
              .filter((d) => d !== "All")
              .filter(
                (d) => selectedDepartment === "All" || d === selectedDepartment,
              )
              .map((dept) => (
                <TableRow key={dept}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      width: 150,
                    }}
                  >
                    <Typography>{dept}</Typography>
                  </TableCell>

                  {[...Array(12).keys()].map((mIndex) => {
                    const intervalsForDept =
                      deptMonthAggregation[mIndex]?.[dept] ?? [];

                    const count = new Set(intervalsForDept.map((i) => i.name))
                      .size;

                    return (
                      <TableCell key={mIndex} align="center">
                        <Tooltip
                          arrow
                          title={buildTooltipContent(intervalsForDept)}
                        >
                          <span>
                            <Button
                              disabled={count === 0}
                              aria-label={`View ${dept} availability for ${new Date(year, mIndex).toLocaleString("en-US", { month: "long" })} ${year}`}
                              onClick={() =>
                                setSelectedIntervals({
                                  department: dept,
                                  month: mIndex,
                                  intervals: intervalsForDept,
                                })
                              }
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              {count === 0 ? "-" : count}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
      <Dialog
        open={!!selectedIntervals}
        onClose={() => setSelectedIntervals(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="yearly-availability-title"
      >
        <DialogTitle id="yearly-availability-title">
          {selectedIntervals &&
            `${selectedIntervals.department} · ${new Date(year, selectedIntervals.month).toLocaleString("en-US", { month: "long", year: "numeric" })}`}
        </DialogTitle>
        <DialogContent sx={{ overflowWrap: "anywhere" }}>
          {selectedIntervals &&
            buildTooltipContent(selectedIntervals.intervals)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedIntervals(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YearlyAvailability;
